import { useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Loader2, Trash2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Stepper } from "@/components/ui/stepper";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import useMediaQuery from "../../hooks/useMediaQuery";
import { parseApiError } from "../../services/apiError";
import { formatNumber, formatTime, planLine, setLine } from "./lib/format";
import {
  DISARM_MS,
  MAX_REPS,
  MAX_RIR,
  buildSetBody,
  formFrom,
  loadKindOf,
  prefillNote,
  prefillSet,
} from "./lib/logSet";
import { dumbbellMax, loadStep } from "./lib/steps";

const PHONE = "(max-width: 767px)";
const FIELDS = ["load", "reps", "rir", "band", "reps_left", "reps_right"];
const RACK_WORDS = {
  barbell: "your barbell's step",
  dumbbells: "your dumbbells' step",
  cable: "your cable stack's step",
  bodyweight: "a small plate",
};

const isPromise = (value) => value && typeof value.then === "function";

/** What − and + move the load by (and up to, for dumbbells), in words. */
function stepHelper(step, exercise, units, max) {
  if (!(step > 0)) return "Type the weight.";
  const rack = exercise?.load_kind === "bodyweight"
    ? "bodyweight"
    : (exercise?.requires ?? []).find((item) => RACK_WORDS[item]);
  const why = RACK_WORDS[rack] ?? "your finest step";
  const upTo = max !== null ? `, up to ${formatNumber(max)} ${units}, your heaviest dumbbell` : "";
  return `− and + move by ${formatNumber(step)} ${units} (${why})${upTo}; or type any weight.`;
}

/**
 * The fields of one set, mounted fresh each time the sheet opens (Radix
 * unmounts a closed sheet's content), so they always start from the
 * prefill or the set being edited.
 */
function SetForm({
  exercise,
  item,
  sets,
  settings,
  units,
  editing,
  setNumber,
  onLog,
  onSave,
  onDelete,
  onDone,
  busy,
  setBusy,
  fieldRef,
}) {
  const id = useId();
  const kind = loadKindOf(exercise);
  const [prefill] = useState(() =>
    editing ? null : prefillSet({ item, exercise, sets, settings, units }),
  );
  const [form, setForm] = useState(() => formFrom(editing ?? prefill, exercise));
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [armed, setArmed] = useState(false);
  const disarmTimer = useRef(null);
  const keepRef = useRef(null);
  const deleteRef = useRef(null);
  const armedRef = useRef(null);
  const refocusDelete = useRef(false);
  // A new set is logged once: Enter again (or a second tap) while the sheet
  // slides away must not log it twice. A log that failed can go again.
  const submitted = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(disarmTimer.current);
    };
  }, []);

  const bands = settings?.bands ?? [];
  const step = loadStep(exercise, settings, units);
  const heaviest = dumbbellMax(exercise, settings, units);
  const showsLoad = kind === "external" || (kind === "bodyweight" && form.mode !== "band");
  const showsBand = kind === "band" || (kind === "bodyweight" && form.mode === "band");
  const loadUnit = exercise.per_dumbbell ? `${units} each` : units;
  const change = (field) => (value) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (errors[field]) setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const failed = (err, fallback) => {
    const { formError: banner, fieldErrors } = parseApiError(err, fallback);
    const placed = {};
    const rest = [];
    for (const [field, value] of Object.entries(fieldErrors)) {
      const text = [].concat(value).join(" ");
      if (FIELDS.includes(field)) placed[field] = text;
      else rest.push(text);
    }
    setErrors(placed);
    setFormError([banner, ...rest].filter(Boolean).join(" ") || null);
  };

  const run = async (kindOfWork, work, fallback) => {
    setBusy(kindOfWork);
    setFormError(null);
    try {
      await work();
      if (mounted.current) onDone();
    } catch (err) {
      if (mounted.current) failed(err, fallback);
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const submit = (event) => {
    event.preventDefault();
    if (busy) return;
    const { body, errors: problems, ok } = buildSetBody(form, exercise);
    if (!ok) {
      setErrors(problems);
      return;
    }
    setErrors({});
    if (editing) {
      run("save", () => onSave(editing.id, body), "Couldn't save the set.");
      return;
    }
    if (submitted.current) return;
    submitted.current = true;
    const result = onLog(body);
    if (isPromise(result)) {
      run("log", () => result.catch((err) => {
        submitted.current = false; // refused: it can go again
        throw err;
      }), "Couldn't log the set.");
    } else onDone();
  };

  useEffect(() => {
    if (armed || !refocusDelete.current) return;
    refocusDelete.current = false;
    deleteRef.current?.focus();
  }, [armed]);

  const arm = () => {
    setArmed(true);
    clearTimeout(disarmTimer.current);
    disarmTimer.current = setTimeout(() => {
      if (!mounted.current) return;
      // Keep it / Delete set go with the question: if focus was there, it
      // goes back to Delete (once drawn) rather than falling to the sheet.
      refocusDelete.current = Boolean(armedRef.current?.contains(document.activeElement));
      setArmed(false);
    }, DISARM_MS);
    setTimeout(() => keepRef.current?.focus(), 0);
  };
  const disarm = () => {
    clearTimeout(disarmTimer.current);
    setArmed(false);
    setTimeout(() => deleteRef.current?.focus(), 0);
  };
  const remove = () => {
    clearTimeout(disarmTimer.current);
    run("delete", () => onDelete(editing.id), "Couldn't delete the set.");
  };

  const note = editing
    ? null
    : prefillNote(prefill, { item, exercise, units });
  const firstField = showsLoad ? "load" : showsBand ? "band" : "reps";

  return (
    <form onSubmit={submit} noValidate className="flex min-h-0 flex-1 flex-col">
      <div data-slot="dialog-body" className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
        {note && <p className="m-0 text-sm text-muted-foreground">{note}</p>}

        {kind === "bodyweight" && (
          <div className="flex flex-col gap-1.5">
            <Label id={`${id}-mode`}>How you did it</Label>
            {/* Two named modes (D17): weight on a belt, or a band's help. */}
            <ToggleGroup
              type="single"
              variant="outline"
              value={form.mode}
              onValueChange={(value) => value && change("mode")(value)}
              aria-labelledby={`${id}-mode`}
              disabled={Boolean(busy)}
              className="justify-start"
            >
              <ToggleGroupItem value="added" className="h-11 px-4 md:h-9">
                Added weight
              </ToggleGroupItem>
              <ToggleGroupItem value="band" className="h-11 px-4 md:h-9" disabled={!bands.length}>
                Band
              </ToggleGroupItem>
            </ToggleGroup>
            {!bands.length && (
              <p className="m-0 text-xs text-muted-foreground">
                No bands in your settings yet — add them in Settings to log a band.
              </p>
            )}
          </div>
        )}

        {showsLoad && (
          <Stepper
            ref={firstField === "load" ? fieldRef : undefined}
            label={kind === "bodyweight" ? "Added weight" : "Load"}
            value={form.load}
            onChange={change("load")}
            step={step}
            min={0}
            max={heaviest ?? undefined}
            unit={loadUnit}
            placeholder={kind === "bodyweight" ? "0 = bodyweight only" : "Type the weight"}
            helper={stepHelper(step, exercise, units, heaviest)}
            error={errors.load}
            disabled={Boolean(busy)}
          />
        )}

        {showsBand && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-band`}>Band</Label>
            <NativeSelect
              ref={firstField === "band" ? fieldRef : undefined}
              id={`${id}-band`}
              value={form.band}
              className="h-11 md:h-9"
              aria-invalid={errors.band ? true : undefined}
              aria-describedby={errors.band ? `${id}-band-error` : `${id}-band-help`}
              disabled={Boolean(busy)}
              onChange={(event) => change("band")(event.target.value)}
            >
              <option value="">Choose a band</option>
              {bands.map((band) => (
                <option key={band.name} value={band.name}>
                  {kind === "bodyweight"
                    ? `${band.name} — ${formatNumber(band.assist)} ${settings?.equipment?.units ?? units} of help`
                    : band.name}
                </option>
              ))}
              {form.band && !bands.some((band) => band.name === form.band) && (
                <option value={form.band}>{form.band} (not in your settings)</option>
              )}
            </NativeSelect>
            {errors.band ? (
              <p id={`${id}-band-error`} className="m-0 text-sm text-destructive">
                {errors.band}
              </p>
            ) : (
              <p id={`${id}-band-help`} className="m-0 text-xs text-muted-foreground">
                {kind === "bodyweight"
                  ? "The band you stood or knelt in — its help comes off your bodyweight."
                  : "The band you used."}
              </p>
            )}
          </div>
        )}

        {exercise.unilateral && (
          <Label className="flex min-h-11 cursor-pointer items-center gap-3 font-normal md:min-h-9">
            <Switch
              checked={form.sides}
              disabled={Boolean(busy)}
              onCheckedChange={(checked) => {
                setForm((current) => ({
                  ...current,
                  sides: checked,
                  left: current.left || current.reps,
                  right: current.right || current.reps,
                }));
              }}
            />
            Left and right were different
          </Label>
        )}

        {exercise.unilateral && form.sides ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Stepper
              ref={firstField === "reps" ? fieldRef : undefined}
              label="Left reps"
              value={form.left}
              onChange={change("left")}
              step={1}
              min={0}
              max={MAX_REPS}
              inputMode="numeric"
              error={errors.reps_left}
              disabled={Boolean(busy)}
            />
            <Stepper
              label="Right reps"
              value={form.right}
              onChange={change("right")}
              step={1}
              min={0}
              max={MAX_REPS}
              inputMode="numeric"
              helper="The set counts the weaker side's reps."
              error={errors.reps_right}
              disabled={Boolean(busy)}
            />
          </div>
        ) : (
          <Stepper
            ref={firstField === "reps" ? fieldRef : undefined}
            label="Reps"
            value={form.reps}
            onChange={change("reps")}
            step={1}
            min={0}
            max={MAX_REPS}
            inputMode="numeric"
            helper={exercise.unilateral ? "Per side." : undefined}
            error={errors.reps}
            disabled={Boolean(busy)}
          />
        )}

        <Stepper
          label="Reps in reserve"
          value={form.rir}
          onChange={change("rir")}
          step={1}
          min={0}
          max={MAX_RIR}
          inputMode="numeric"
          decreaseLabel="Less in reserve"
          increaseLabel="More in reserve"
          helper="How many more you could have done: 0 none, 5 means 5 or more. Leave it empty if you're unsure."
          error={errors.rir}
          disabled={Boolean(busy)}
        />

        {formError && (
          <Alert variant="destructive">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
      </div>

      <SheetFooter className="border-t">
        {editing ? (
          <>
            <Button type="submit" className="h-11 w-full md:h-10" disabled={Boolean(busy)}>
              {busy === "save" && <Loader2 className="animate-spin" aria-hidden="true" />}
              {busy === "save" ? "Saving…" : "Save changes"}
            </Button>
            {editing.can_delete !== false &&
              (armed ? (
                <div
                  ref={armedRef}
                  role="group"
                  aria-label="Delete this set?"
                  className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 p-2"
                >
                  <span className="flex-1 text-sm">Delete this set? It&apos;s removed from the session.</span>
                  <Button
                    type="button"
                    variant="destructive"
                    className="h-11 md:h-9"
                    disabled={Boolean(busy)}
                    onClick={remove}
                  >
                    {busy === "delete" && <Loader2 className="animate-spin" aria-hidden="true" />}
                    {busy === "delete" ? "Deleting…" : "Delete set"}
                  </Button>
                  <Button
                    ref={keepRef}
                    type="button"
                    variant="outline"
                    className="h-11 md:h-9"
                    disabled={Boolean(busy)}
                    onClick={disarm}
                  >
                    Keep it
                  </Button>
                </div>
              ) : (
                <Button
                  ref={deleteRef}
                  type="button"
                  variant="outline"
                  className="h-11 w-full border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive md:h-10"
                  disabled={Boolean(busy)}
                  onClick={arm}
                >
                  <Trash2 aria-hidden="true" />
                  Delete set {setNumber}
                </Button>
              ))}
          </>
        ) : (
          <>
            <Button
              type="submit"
              className="h-11 w-full md:h-10"
              disabled={Boolean(busy)}
              onClick={(event) => {
                // a double-click is two clicks: the second must not log again
                if (event.detail > 1) event.preventDefault();
              }}
            >
              {busy === "log" && <Loader2 className="animate-spin" aria-hidden="true" />}
              {busy === "log" ? "Logging…" : "Log set"}
            </Button>
            <p className="m-0 text-xs text-muted-foreground">
              Saved on this device at once and sent to mono (again later if there&apos;s no
              signal); the rest timer starts.
            </p>
          </>
        )}
      </SheetFooter>
    </form>
  );
}

SetForm.propTypes = {
  exercise: PropTypes.object.isRequired,
  item: PropTypes.object,
  sets: PropTypes.arrayOf(PropTypes.object).isRequired,
  settings: PropTypes.object,
  units: PropTypes.string.isRequired,
  editing: PropTypes.object,
  setNumber: PropTypes.number.isRequired,
  onLog: PropTypes.func,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  onDone: PropTypes.func.isRequired,
  busy: PropTypes.string,
  setBusy: PropTypes.func.isRequired,
  fieldRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
};

/**
 * Log one set — or change or delete one already logged (walkthrough 5).
 * From the bottom on a phone, the side on a desktop.
 *
 * A new set starts filled in (lib/logSet.js prefillSet): the first from the
 * plan's target, later ones from the previous set of the exercise this
 * session — or, while the weight is being found (D9), from the next set's
 * suggestion, with the reason in words. So *Log set* is one tap when it
 * went as planned. − and + step the load by the exercise's real increment
 * (the barbell, dumbbell or cable step from the settings; 5 lb / 2.5 kg of
 * added weight); the field takes any typed weight (a decimal keypad).
 * Bodyweight moves are *Added weight* or *Band* (D17); band moves take a
 * band; unilateral moves can log left and right reps when they differed.
 *
 * Contract (shared with History):
 * - `onLog(body)`: a new set — a TrainingSetWrite without `client_id`. If it
 *   returns a promise the sheet waits (and shows a failure); otherwise it
 *   closes at once (the session's queue takes it from there).
 * - `editing` (a TrainingSet) switches to editing: `onSave(setId, body)`
 *   and `onDelete(setId)` return promises; a refusal's words land on the
 *   field they name. Delete asks once, inside the sheet, and the question
 *   puts itself away after a few seconds.
 * - The sheet closes itself after a log, a save or a delete (through
 *   `onOpenChange(false)`). While a request runs it can't be closed.
 */
function LogSetSheet({
  open,
  onOpenChange,
  exercise,
  item = null,
  sets,
  settings,
  units: givenUnits,
  onLog,
  onSave,
  onDelete,
  editing = null,
  onCloseAutoFocus,
}) {
  const isPhone = useMediaQuery(PHONE);
  const [busy, setBusy] = useState(null);
  const fieldRef = useRef(null);
  const units = givenUnits ?? settings?.units ?? "lb";
  const name = exercise.name ?? exercise.key;
  const editIndex = editing ? sets.findIndex((set) => set.id === editing.id) : -1;
  const setNumber = editing ? (editIndex === -1 ? sets.length : editIndex + 1) : sets.length + 1;

  const close = () => {
    if (!busy) onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      {/* data-training: portalled out of the layout's wrapper, so it
          carries the scope that keeps its muted text AA (tailwind.css). */}
      <SheetContent
        data-training=""
        side={isPhone ? "bottom" : "right"}
        showCloseButton={false}
        className={
          isPhone
            ? "max-h-[90dvh] gap-0 rounded-t-lg pb-[env(safe-area-inset-bottom)]"
            : "w-full gap-0 sm:max-w-md"
        }
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => busy && event.preventDefault()}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          fieldRef.current?.focus();
        }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <SheetHeader className="flex-row items-start gap-3 border-b">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <SheetTitle className="font-display text-xl">
              {/* "set N" never wraps apart (a lone number on its own line);
                  nowrap, not a no-break space, so the name reads as typed. */}
              {editing ? (
                <>
                  Edit <span className="whitespace-nowrap">set {setNumber}</span> of {name}
                </>
              ) : (
                <>
                  {name}: <span className="whitespace-nowrap">set {setNumber}</span>
                </>
              )}
            </SheetTitle>
            <SheetDescription className="tabular-nums">
              {editing
                ? `Logged${editing.performed_at ? ` at ${formatTime(editing.performed_at, settings?.timezone)}` : ""}: ${setLine(editing, units, exercise)}.`
                : item
                  ? `Planned: ${planLine(item, units, exercise)}.`
                  : "Not in today's plan."}
            </SheetDescription>
          </div>
          {/* The sheet's own X is 16px; this is the 44px way out. */}
          <Button
            type="button"
            variant="ghost"
            className="h-11 shrink-0 md:h-9"
            disabled={Boolean(busy)}
            onClick={close}
          >
            Cancel
          </Button>
        </SheetHeader>
        <SetForm
          exercise={exercise}
          item={item}
          sets={sets}
          settings={settings}
          units={units}
          editing={editing}
          setNumber={setNumber}
          onLog={onLog}
          onSave={onSave}
          onDelete={onDelete}
          onDone={() => onOpenChange(false)}
          busy={busy}
          setBusy={setBusy}
          fieldRef={fieldRef}
        />
      </SheetContent>
    </Sheet>
  );
}

LogSetSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  /** The catalog's exercise (TrainingExercise) — at least {key, name}. */
  exercise: PropTypes.shape({
    key: PropTypes.string.isRequired,
    name: PropTypes.string,
    load_kind: PropTypes.string,
    unilateral: PropTypes.bool,
    per_dumbbell: PropTypes.bool,
    requires: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  /** Its plan item (TrainingPlanItem), when the plan has it. */
  item: PropTypes.object,
  /** This session's sets of the exercise, oldest first (queued ones too). */
  sets: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** TrainingSettings: the load steps, the bands, the time zone. */
  settings: PropTypes.object,
  /** The session's units (default: the settings'). */
  units: PropTypes.string,
  /** A new set: `onLog(body)`. */
  onLog: PropTypes.func,
  /** Editing: `onSave(setId, body)` → promise. */
  onSave: PropTypes.func,
  /** Editing: `onDelete(setId)` → promise. */
  onDelete: PropTypes.func,
  /** The set being edited (TrainingSet); none to log a new one. */
  editing: PropTypes.object,
  /** Where focus goes when it closes (default: whatever opened it). */
  onCloseAutoFocus: PropTypes.func,
};

export default LogSetSheet;
